/**
 * MySubscriptionDrawer — a read-only, right-side panel the company OWNER/ADMIN
 * opens from the header status chip. Shows their own plan, next billing date,
 * days remaining, last payment + history, the features they're using, and the
 * add-ons they could get (with a WhatsApp "ask us" link). No actions — info only.
 */

import React from 'react';
import styled from 'styled-components';
import { FiX, FiCheck } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import {
  ALL_MODULES, MODULE_GROUPS, isModuleEnabledForCompany,
  BRANCH_MODULE_ID, POS_MODULE_IDS, CUSTOMER_PRICING_MODULE_ID, FIXED_ASSETS_MODULE_IDS,
} from '../config/moduleRegistry';

const WHATSAPP_URL = 'https://wa.me/923353058349?text=' +
  encodeURIComponent("Hi, I'd like to know more about AmalERP add-ons / my subscription.");

// Curated, sellable add-ons (friendly name + benefit + a representative module id).
const ADD_ONS = [
  { name: 'WhatsApp Integration', benefit: 'Send invoices & receipts to customers automatically on WhatsApp.', id: 'whatsapp_connect' },
  { name: 'Point of Sale (POS)', benefit: 'Fast retail billing screen with counters, shifts & receipts.', id: POS_MODULE_IDS[0] },
  { name: 'Multi-Branch', benefit: 'Run multiple branches with separate stock & reporting.', id: BRANCH_MODULE_ID },
  { name: 'Fixed Assets', benefit: 'Track assets, run depreciation & record disposals.', id: FIXED_ASSETS_MODULE_IDS[0] },
  { name: 'Manual Production', benefit: 'Issue raw materials and receive finished goods (no BOM needed).', id: 'store_material_issue' },
  { name: 'Customer Price Lists', benefit: 'Per-customer pricing auto-filled onto invoices.', id: CUSTOMER_PRICING_MODULE_ID },
  { name: 'POS Weight Scale', benefit: 'Capture weight from an electronic scale straight into POS.', id: 'pos_weight_scale' },
  { name: 'Barcode Labels', benefit: 'Design & print barcode labels for your items.', id: 'master_barcode_labels' },
  { name: 'Document Attachments', benefit: 'Attach files (PDFs, images) to any transaction.', id: 'transaction_attachments' },
  { name: 'Cash Book', benefit: 'A dedicated day-to-day cash book view.', id: 'cash_book' },
];

const DAY = 24 * 60 * 60 * 1000;

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(dt)) return '—';
  return dt.toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' });
};

const MySubscriptionDrawer = ({ open, onClose, company }) => {
  if (!open || !company) return null;

  const sub = company.subscription || {};
  const plan = sub.plan || 'trial';
  const now = new Date();
  const currency = company.currency?.symbol || company.currencySymbol || 'Rs.';
  const money = (n) => `${currency} ${Number(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  // ── Billing / status ──
  let statusLabel = 'Active', statusType = 'active', remainingDays = null, nextDate = null;
  if (plan === 'trial') {
    const trialEnd = sub.trialEndDate ? new Date(sub.trialEndDate) : new Date(new Date(company.createdAt).getTime() + 7 * DAY);
    nextDate = trialEnd;
    remainingDays = Math.ceil((trialEnd - now) / DAY);
    if (remainingDays <= 0) { statusLabel = 'Trial Expired'; statusType = 'expired'; remainingDays = 0; }
    else { statusLabel = `Trial — ${remainingDays}d left`; statusType = 'trial'; }
  } else {
    nextDate = sub.nextPaymentDate ? new Date(sub.nextPaymentDate) : null;
    remainingDays = nextDate ? Math.ceil((nextDate - now) / DAY) : null;
    if (nextDate && nextDate < now) { statusLabel = 'Expired'; statusType = 'expired'; }
    else {
      const planName = { monthly: 'Monthly', quarterly: 'Quarterly', 'half-yearly': 'Half-Yearly', yearly: 'Yearly' }[plan];
      statusLabel = planName ? `Active · ${planName}` : 'Active';
      statusType = 'active';
    }
  }
  const remColor = remainingDays == null ? '#666' : remainingDays <= 0 ? '#C62828' : remainingDays <= 7 ? '#E65100' : '#2E7D32';

  const history = Array.isArray(sub.paymentHistory) ? sub.paymentHistory.slice().reverse() : [];
  const lifetimePaid = (Array.isArray(sub.paymentHistory) ? sub.paymentHistory : [])
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);

  // ── Features in use ──
  const enabledModules = company.enabledModules;
  const toggleable = ALL_MODULES;
  const usingCount = toggleable.filter((m) => isModuleEnabledForCompany(m.id, enabledModules)).length;

  // ── Add-ons available (not enabled) ──
  const availableAddons = ADD_ONS.filter((a) => a.id && !isModuleEnabledForCompany(a.id, enabledModules));

  return (
    <Overlay onClick={onClose}>
      <Drawer onClick={(e) => e.stopPropagation()}>
        <Head>
          <div>
            <Company>{company.companyName || 'My Company'}</Company>
            <Pill $type={statusType}>{statusLabel}</Pill>
          </div>
          <CloseBtn onClick={onClose}><FiX /></CloseBtn>
        </Head>

        <Body>
          {/* Billing */}
          <Section>
            <SectionTitle>Subscription</SectionTitle>
            <Grid>
              <Cell><K>Plan</K><V style={{ textTransform: 'capitalize' }}>{plan}</V></Cell>
              <Cell><K>{plan === 'trial' ? 'Trial ends' : 'Next billing'}</K><V>{fmtDate(nextDate)}</V></Cell>
              <Cell><K>Days remaining</K><V style={{ color: remColor, fontWeight: 800 }}>{remainingDays == null ? '—' : remainingDays <= 0 ? 'Overdue' : `${remainingDays} days`}</V></Cell>
              <Cell><K>Last paid</K><V>{sub.amount ? money(sub.amount) : '—'}{sub.lastPaymentDate ? ` · ${fmtDate(sub.lastPaymentDate)}` : ''}</V></Cell>
            </Grid>
          </Section>

          {/* Payment history */}
          {history.length > 0 && (
            <Section>
              <SectionTitle>Payment history <Muted>· lifetime {money(lifetimePaid)}</Muted></SectionTitle>
              <HistoryList>
                {history.map((p, i) => (
                  <HistRow key={i}>
                    <span>{fmtDate(p.date || p.paymentDate || p.paidOn)}</span>
                    <span style={{ fontWeight: 700 }}>{money(p.amount)}</span>
                  </HistRow>
                ))}
              </HistoryList>
            </Section>
          )}

          {/* Features in use */}
          <Section>
            <SectionTitle>Features you're using <Muted>· {usingCount} of {toggleable.length}</Muted></SectionTitle>
            <Groups>
              {MODULE_GROUPS.map((g) => {
                const on = g.modules.filter((m) => isModuleEnabledForCompany(m.id, enabledModules));
                if (on.length === 0) return null;
                return (
                  <GroupBlock key={g.name}>
                    <GroupName>{g.name}</GroupName>
                    <Chips>
                      {on.map((m) => <Chip key={m.id}><FiCheck size={11} /> {m.name}</Chip>)}
                    </Chips>
                  </GroupBlock>
                );
              })}
            </Groups>
          </Section>

          {/* Add-ons */}
          {availableAddons.length > 0 && (
            <Section>
              <SectionTitle>Add-ons you can get</SectionTitle>
              <AddonList>
                {availableAddons.map((a) => (
                  <AddonRow key={a.name}>
                    <div>
                      <AddonName>{a.name}</AddonName>
                      <AddonBenefit>{a.benefit}</AddonBenefit>
                    </div>
                  </AddonRow>
                ))}
              </AddonList>
              <WaCta href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <FaWhatsapp size={16} /> Interested? Message us on WhatsApp
              </WaCta>
            </Section>
          )}
        </Body>
      </Drawer>
    </Overlay>
  );
};

// ── Styles ──
const Overlay = styled.div`position: fixed; inset: 0; z-index: 9500; background: rgba(0,0,0,0.4); display: flex; justify-content: flex-end;`;
const Drawer = styled.div`
  width: 100%; max-width: 420px; height: 100%; background: #fff;
  box-shadow: -8px 0 32px rgba(0,0,0,0.2); display: flex; flex-direction: column;
  animation: slideIn 0.2s ease-out;
  @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
`;
const Head = styled.div`display: flex; align-items: flex-start; justify-content: space-between; padding: 20px; border-bottom: 1px solid #eee;`;
const Company = styled.div`font-size: 17px; font-weight: 800; color: #1A1A1A; margin-bottom: 8px;`;
const Pill = styled.span`
  display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 700;
  ${(p) => p.$type === 'active' ? 'background:#E8F5E9;color:#2E7D32;' : p.$type === 'trial' ? 'background:#FFF3E0;color:#E65100;' : 'background:#FFEBEE;color:#C62828;'}
`;
const CloseBtn = styled.button`background: none; border: none; cursor: pointer; font-size: 22px; color: #666; display: flex;`;
const Body = styled.div`flex: 1; overflow-y: auto; padding: 20px;`;
const Section = styled.div`margin-bottom: 24px;`;
const SectionTitle = styled.div`font-size: 13px; font-weight: 800; color: #1A1A1A; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 12px;`;
const Muted = styled.span`font-weight: 600; color: #999; text-transform: none; letter-spacing: 0;`;
const Grid = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 14px;`;
const Cell = styled.div`display: flex; flex-direction: column; gap: 3px;`;
const K = styled.div`font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.3px;`;
const V = styled.div`font-size: 14px; font-weight: 600; color: #1A1A1A;`;
const HistoryList = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const HistRow = styled.div`display: flex; justify-content: space-between; font-size: 13px; color: #444; padding: 8px 10px; background: #f7f7f7; border-radius: 6px;`;
const Groups = styled.div`display: flex; flex-direction: column; gap: 12px;`;
const GroupBlock = styled.div``;
const GroupName = styled.div`font-size: 12px; font-weight: 700; color: #555; margin-bottom: 6px;`;
const Chips = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;
const Chip = styled.span`display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; background: #EEF6EE; color: #2E7D32; border-radius: 5px; font-size: 11px; font-weight: 600;`;
const AddonList = styled.div`display: flex; flex-direction: column; gap: 8px;`;
const AddonRow = styled.div`padding: 10px 12px; background: #f7f7f7; border: 1px solid #eee; border-radius: 8px;`;
const AddonName = styled.div`font-size: 14px; font-weight: 700; color: #1A1A1A;`;
const AddonBenefit = styled.div`font-size: 12px; color: #777; margin-top: 2px; line-height: 1.4;`;
const WaCta = styled.a`
  display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 12px;
  padding: 11px; background: #25D366; color: #fff; border-radius: 8px; font-size: 14px; font-weight: 700;
  text-decoration: none; &:hover { opacity: 0.92; }
`;

export default MySubscriptionDrawer;
